/**
 * A parent's locked device, let in by one of the family's unlocked devices.
 *
 * For the parent who forgot the family code. Settings shows the code on any
 * unlocked device, so forgetting it only ever hurt on a device that could not
 * unlock -- which is exactly where you are standing when you find out. This
 * is the way in from there that needs no code:
 *
 *   locked device    --keypair (the same one a child's tablet makes)-->
 *                      asks, with its public key; shows a match code
 *   unlocked device  --sees "mum wants to unlock another device", checks
 *                      the one-time code, wraps the FAMILY key to that
 *                      public key-->  server (bytes it cannot open)
 *   locked device    <--unwraps with a private key that never left it
 *
 * Section 20's child approval, pointed at a parent and carrying the family
 * key rather than a person key. Server side: linking.py, "a parent's device,
 * let in by another parent's device".
 *
 * Asking shows a ONE-TIME CODE. The parent reads it out to whoever has an
 * unlocked device, who types it in Settings -> Members (or says yes to the
 * request on Home, which shows the same code). The request lasts a week and
 * is found again by account and device after signing out -- `myKeyRequest`.
 */

import { api } from '../api/client';
import { decryptField } from '../crypto/familyKey';
import {
  deviceKeypair,
  importPublicKey,
  publicKeyBytes,
  unwrapFromDevice,
  wrapForDevice,
} from '../crypto/deviceKey';
import { rememberKey } from '../crypto/vault';

export interface AskedForKey {
  id: string;
  /** The one-time code this screen shows, as XXXX-XXXX. */
  code: string;
}

/** Ask -- or, from a device that already asked, get that request back. */
export async function askForKey(familyId: string): Promise<AskedForKey> {
  const devicePub = await publicKeyBytes(await deviceKeypair());
  const asked = await api.write<{ id: string; code: string }>(
    'POST',
    `/v1/families/${familyId}/key-requests`,
    { devicePub },
  );
  return { id: asked.id, code: asked.code };
}

/**
 * On the way in, locked: has this device asked, and has anybody said yes?
 * Survives signing out -- the server finds it by account and device key. A
 * yes is opened, kept on this device, and returned as the key.
 */
export async function myKeyRequest(
  familyId: string,
): Promise<{ status: 'approved'; key: CryptoKey } | { status: 'pending'; asked: AskedForKey } | null> {
  const pair = await deviceKeypair();
  const devicePub = await publicKeyBytes(pair);
  const mine = await api.write<{ status: string | null; id?: string; code?: string; keyWrap?: string }>(
    'POST',
    `/v1/families/${familyId}/key-requests/mine`,
    { devicePub },
  );
  if (mine.status === 'approved' && mine.keyWrap) {
    const key = await unwrapFromDevice(mine.keyWrap, pair);
    await rememberKey(familyId, key);
    return { status: 'approved', key };
  }
  if (mine.status === 'pending' && mine.id && mine.code) {
    return { status: 'pending', asked: { id: mine.id, code: mine.code } };
  }
  return null;
}

export type KeyRequestOutcome =
  | { status: 'pending' }
  | { status: 'denied' }
  | { status: 'approved'; key: CryptoKey };

/** Has anybody answered? On a yes, the key is opened and kept on this device. */
export async function checkKeyRequest(
  familyId: string,
  id: string,
): Promise<KeyRequestOutcome> {
  const result = await api.get<{ status: string; keyWrap?: string }>(
    `/v1/families/${familyId}/key-requests/${id}`,
  );
  if (result.status === 'approved' && result.keyWrap) {
    const key = await unwrapFromDevice(result.keyWrap, await deviceKeypair());
    await rememberKey(familyId, key);
    return { status: 'approved', key };
  }
  return result.status === 'denied' ? { status: 'denied' } : { status: 'pending' };
}

export async function withdrawKeyRequest(familyId: string, id: string): Promise<void> {
  await api.write('DELETE', `/v1/families/${familyId}/key-requests/${id}`);
}

export interface KeyRequest {
  id: string;
  /** The asking account's own email, opened here; empty if it left none. */
  email: string;
  sub: string;
  /** When that account joined the family, for telling parents apart without an email. */
  since?: string;
  /** The one-time code the asking screen shows. */
  code: string;
  devicePub: string;
  created?: string;
}

type RawRequest = {
  id: string;
  sub: string;
  emailCt?: string | null;
  since?: string;
  code: string;
  devicePub: string;
  created?: string;
};

async function opened(key: CryptoKey, { emailCt, ...request }: RawRequest): Promise<KeyRequest> {
  return {
    ...request,
    email: emailCt ? await decryptField(key, emailCt).catch(() => '') : '',
  };
}

/** What an unlocked device is being asked. */
export async function listKeyRequests(
  familyId: string,
  key: CryptoKey,
): Promise<KeyRequest[]> {
  const result = await api.get<{ requests: RawRequest[] }>(`/v1/families/${familyId}/key-requests`);
  return Promise.all(result.requests.map((request) => opened(key, request)));
}

/** The code a parent read out, typed here. Throws if nothing is waiting with it. */
export async function findKeyRequest(
  familyId: string,
  key: CryptoKey,
  code: string,
): Promise<KeyRequest> {
  const found = await api.write<RawRequest>('POST', `/v1/families/${familyId}/key-requests/find`, {
    code,
  });
  return opened(key, found);
}

/** Yes, with the family key wrapped to the asking device -- or no. */
export async function answerKeyRequest(
  familyId: string,
  key: CryptoKey,
  request: KeyRequest,
  approve: boolean,
): Promise<void> {
  const body = approve
    ? { approve: true, keyWrap: await wrapForDevice(key, await importPublicKey(request.devicePub)) }
    : { approve: false };
  await api.write('POST', `/v1/families/${familyId}/key-requests/${request.id}/answer`, body);
}
